import { memo, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNotify, usePermissions, useTranslate } from 'react-admin';

import {
  Alert,
  Button,
  Card,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { DashboardContent } from 'src/layouts/dashboard';
import { Iconify } from 'src/components/iconify';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { PermissionButton } from 'src/routes/components';
import type { PermissionData } from 'src/pages/admin/entities/permission.entity';
import { hasPermission } from 'src/pages/admin/utils/has-permission';

import { usePredictionVoucherRule } from './hooks/usePredictionVoucherRule';
import type {
  PredictionVoucherRule,
  PredictionVoucherTierDraft,
  PredictionVoucherTierDraftMap,
  SaveBarProps,
  TranslateFn,
  VoucherTierTableProps,
} from './types';
import {
  buildPayload,
  createEmptyTier,
  formatDateInput,
  getDefaultRule,
  getDefaultTierMap,
  getErrorMessage,
  isBundleEqual,
  toDraftMap,
  toNumber,
} from './utils';

const I18N_PREFIX = 'module.worldCup.predictionVoucher';

const tVoucher = (t: TranslateFn, key: string, options?: Record<string, unknown>) =>
  t(`${I18N_PREFIX}.${key}`, options);

export const MainPage = () => {
  const t = useTranslate();
  const notify = useNotify();

  const { rule, tiers, loading, saving, refetch, replaceRule } = usePredictionVoucherRule();

  const [ruleDraft, setRuleDraft] = useState<PredictionVoucherRule>(getDefaultRule());
  const [originalRuleDraft, setOriginalRuleDraft] =
    useState<PredictionVoucherRule>(getDefaultRule());

  const [tierDrafts, setTierDrafts] = useState<PredictionVoucherTierDraftMap>(
    toDraftMap(getDefaultTierMap())
  );
  const [originalTierDrafts, setOriginalTierDrafts] = useState<PredictionVoucherTierDraftMap>(
    toDraftMap(getDefaultTierMap())
  );

  useEffect(() => {
    const nextRule = {
      ...rule,
      effectiveFrom: formatDateInput(rule.effectiveFrom),
      effectiveTo: formatDateInput(rule.effectiveTo),
    };
    const nextTiers = toDraftMap(tiers);

    setRuleDraft(nextRule);
    setOriginalRuleDraft(nextRule);
    setTierDrafts(nextTiers);
    setOriginalTierDrafts(nextTiers);
  }, [rule, tiers]);

  const hasUnsavedChanges = useMemo(
    () => !isBundleEqual(ruleDraft, tierDrafts, originalRuleDraft, originalTierDrafts),
    [originalRuleDraft, originalTierDrafts, ruleDraft, tierDrafts]
  );

  const handleUpdateTiers = useCallback(
    (channel: keyof PredictionVoucherTierDraftMap, rows: PredictionVoucherTierDraft[]) => {
      setTierDrafts((prev) => ({
        ...prev,
        [channel]: rows,
      }));
    },
    []
  );

  const handleRuleChange = useCallback((patch: Partial<PredictionVoucherRule>) => {
    setRuleDraft((prev) => ({
      ...prev,
      ...patch,
    }));
  }, []);

  const handleDiscard = useCallback(() => {
    if (!window.confirm(tVoucher(t, 'confirm.discard'))) return;

    setRuleDraft(originalRuleDraft);
    setTierDrafts(originalTierDrafts);
  }, [originalRuleDraft, originalTierDrafts, t]);

  const handleSave = useCallback(async () => {
    try {
      await replaceRule(buildPayload(ruleDraft, tierDrafts));
      await refetch();

      notify(tVoucher(t, 'notify.saveSuccess'), { type: 'success' });
    } catch (err) {
      notify(getErrorMessage(err) || tVoucher(t, 'notify.saveFailed'), { type: 'error' });
    }
  }, [notify, refetch, replaceRule, ruleDraft, t, tierDrafts]);

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={tVoucher(t, 'title')}
        links={[
          { name: t('resource.worldCup') },
          { name: t('resource.worldCupPredictionVoucherRule') },
        ]}
        sx={{ mb: 3 }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {tVoucher(t, 'description')}
      </Typography>

      {loading ? (
        <CenterBox>
          <CircularProgress size={28} />
        </CenterBox>
      ) : (
        <Stack spacing={3}>
          <VoucherTierTable
            title={tVoucher(t, 'futures.title')}
            description={tVoucher(t, 'futures.desc')}
            rows={tierDrafts.futures}
            onRowsChange={(rows) => handleUpdateTiers('futures', rows)}
          />

          <VoucherTierTable
            title={tVoucher(t, 'predictionMarket.title')}
            description={tVoucher(t, 'predictionMarket.desc')}
            rows={tierDrafts.predictionMarket}
            onRowsChange={(rows) => handleUpdateTiers('predictionMarket', rows)}
          />

          <VoucherTierTable
            title={tVoucher(t, 'predictionLeverage.title')}
            description={tVoucher(t, 'predictionLeverage.desc')}
            rows={tierDrafts.predictionLeverage}
            onRowsChange={(rows) => handleUpdateTiers('predictionLeverage', rows)}
          />

          <RuleLimitPanel rule={ruleDraft} onChange={handleRuleChange} />

          <EffectiveWindowPanel rule={ruleDraft} onChange={handleRuleChange} />
        </Stack>
      )}

      {hasUnsavedChanges ? (
        <SaveBar loading={saving} onDiscard={handleDiscard} onSave={handleSave} />
      ) : null}
    </DashboardContent>
  );
};

const VoucherTierTable = memo(
  ({ title, description, rows, onRowsChange }: VoucherTierTableProps) => {
    const t = useTranslate();

    const handleAdd = () => {
      const last = rows[rows.length - 1];

      onRowsChange([...rows, createEmptyTier(last)]);
    };

    const handleRemove = (localId: string) => {
      onRowsChange(rows.filter((item) => item.localId !== localId));
    };

    const handleRowChange = (localId: string, patch: Partial<PredictionVoucherTierDraft>) => {
      onRowsChange(rows.map((item) => (item.localId === localId ? { ...item, ...patch } : item)));
    };

    return (
      <Card sx={{ p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 2 }}
        >
          <Stack>
            <Typography variant="h6">{title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Stack>

          <Button
            variant="outlined"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={handleAdd}
          >
            {tVoucher(t, 'actions.addTier')}
          </Button>
        </Stack>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{tVoucher(t, 'table.tier')}</TableCell>
                <TableCell>{tVoucher(t, 'table.threshold')}</TableCell>
                <TableCell>{tVoucher(t, 'table.voucherCount')}</TableCell>
                <TableCell>{tVoucher(t, 'table.action')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length ? (
                rows.map((row, index) => (
                  <TableRow key={row.localId}>
                    <TableCell>
                      <TierBadge index={index} />
                    </TableCell>
                    <TableCell>
                      <MoneyInput
                        value={row.volumeThresholdU}
                        onChange={(value) =>
                          handleRowChange(row.localId, { volumeThresholdU: value })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <NumberInput
                        value={row.voucherCount}
                        suffix={tVoucher(t, 'unit.ticket')}
                        onChange={(value) => handleRowChange(row.localId, { voucherCount: value })}
                      />
                    </TableCell>
                    <TableCell>
                      <PermissionButton resource="world-cup-prediction-voucher" action="write">
                        <Button
                          variant="text"
                          color="error"
                          size="small"
                          onClick={() => handleRemove(row.localId)}
                        >
                          {tVoucher(t, 'actions.delete')}
                        </Button>
                      </PermissionButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <EmptyText text={tVoucher(t, 'empty.tiers')} />
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    );
  }
);

VoucherTierTable.displayName = 'VoucherTierTable';

const RuleLimitPanel = memo(
  ({
    rule,
    onChange,
  }: {
    rule: PredictionVoucherRule;
    onChange: (patch: Partial<PredictionVoucherRule>) => void;
  }) => {
    const t = useTranslate();
    const { permissions } = usePermissions<PermissionData>();
    const canWrite = hasPermission(
      permissions?.permissions,
      'world-cup-prediction-voucher',
      'write'
    );

    return (
      <Card sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {tVoucher(t, 'limits.title')}
        </Typography>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Field label={tVoucher(t, 'limits.perUserDailyCap')}>
            <NumberInput
              value={rule.perUserDailyCap}
              suffix={tVoucher(t, 'unit.ticket')}
              onChange={(value) => onChange({ perUserDailyCap: value })}
            />
          </Field>

          <Field label={tVoucher(t, 'limits.perUserEventCap')}>
            <NumberInput
              value={rule.perUserEventCap}
              suffix={tVoucher(t, 'unit.ticket')}
              onChange={(value) => onChange({ perUserEventCap: value })}
            />
          </Field>

          <Field label={tVoucher(t, 'limits.eventTotalCap')}>
            <NumberInput
              value={rule.eventTotalCap}
              suffix={tVoucher(t, 'unit.ticket')}
              onChange={(value) => onChange({ eventTotalCap: value })}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {tVoucher(t, 'limits.eventTotalCapHint')}
            </Typography>
          </Field>
        </Stack>

        <Paper
          variant="outlined"
          sx={{
            mt: 2,
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack>
            <Typography variant="subtitle2">{tVoucher(t, 'limits.totalBudgetEnforced')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {tVoucher(t, 'limits.totalBudgetEnforcedDesc')}
            </Typography>
          </Stack>

          <Switch
            checked={rule.totalBudgetEnforced}
            disabled={!canWrite}
            onChange={(_, checked) => onChange({ totalBudgetEnforced: checked })}
          />
        </Paper>
      </Card>
    );
  }
);

RuleLimitPanel.displayName = 'RuleLimitPanel';

const EffectiveWindowPanel = memo(
  ({
    rule,
    onChange,
  }: {
    rule: PredictionVoucherRule;
    onChange: (patch: Partial<PredictionVoucherRule>) => void;
  }) => {
    const t = useTranslate();

    return (
      <Card sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {tVoucher(t, 'effective.title')}
        </Typography>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Field label={tVoucher(t, 'effective.from')}>
            <TextInput
              value={rule.effectiveFrom}
              onChange={(value) => onChange({ effectiveFrom: value })}
            />
          </Field>

          <Field label={tVoucher(t, 'effective.to')}>
            <TextInput
              value={rule.effectiveTo}
              onChange={(value) => onChange({ effectiveTo: value })}
            />
          </Field>
        </Stack>
      </Card>
    );
  }
);

EffectiveWindowPanel.displayName = 'EffectiveWindowPanel';

const TierBadge = memo(({ index }: { index: number }) => {
  const colorMap: Array<'info' | 'primary' | 'warning' | 'default'> = [
    'info',
    'primary',
    'warning',
  ];
  const color = colorMap[index] ?? 'default';

  return <Chip label={`Tier ${index + 1}`} color={color} size="small" />;
});

TierBadge.displayName = 'TierBadge';

const Field = memo(({ label, children }: { label: string; children: ReactNode }) => (
  <Stack sx={{ flex: 1 }}>
    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
      {label}
    </Typography>
    {children}
  </Stack>
));

Field.displayName = 'Field';

const TextInput = memo(
  ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
    <TextField
      size="small"
      fullWidth
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  )
);

TextInput.displayName = 'TextInput';

const MoneyInput = memo(
  ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
    <TextField
      size="small"
      fullWidth
      value={value}
      onChange={(event) => onChange(event.target.value.replace(/[^\d.]/g, ''))}
      InputProps={{
        startAdornment: <InputAdornment position="start">$</InputAdornment>,
      }}
      inputProps={{ inputMode: 'decimal' }}
    />
  )
);

MoneyInput.displayName = 'MoneyInput';

const NumberInput = memo(
  ({
    value,
    suffix,
    onChange,
  }: {
    value: number;
    suffix?: string;
    onChange: (value: number) => void;
  }) => (
    <TextField
      type="number"
      size="small"
      fullWidth
      value={value}
      onChange={(event) => onChange(toNumber(event.target.value))}
      onKeyDown={(event) => {
        if (['e', 'E', '+', '-'].includes(event.key)) event.preventDefault();
      }}
      InputProps={{
        endAdornment: suffix ? <InputAdornment position="end">{suffix}</InputAdornment> : undefined,
      }}
      inputProps={{ inputMode: 'numeric' }}
    />
  )
);

NumberInput.displayName = 'NumberInput';

const SaveBar = memo(({ loading, onDiscard, onSave }: SaveBarProps) => {
  const t = useTranslate();

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'sticky',
        bottom: 16,
        mt: 3,
        px: 3,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <Stack>
        <Typography variant="subtitle2">{tVoucher(t, 'saveBar.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {tVoucher(t, 'saveBar.desc')}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1.5}>
        <Button variant="outlined" disabled={loading} onClick={onDiscard}>
          {tVoucher(t, 'actions.discard')}
        </Button>

        <PermissionButton resource="world-cup-prediction-voucher" action="write">
          <Button variant="contained" disabled={loading} onClick={onSave}>
            {loading ? t('common.saving') : tVoucher(t, 'actions.save')}
          </Button>
        </PermissionButton>
      </Stack>
    </Paper>
  );
});

SaveBar.displayName = 'SaveBar';

const ErrorCard = memo(({ message }: { message: string }) => {
  const t = useTranslate();

  return (
    <Alert severity="error">
      <Typography variant="subtitle2">{tVoucher(t, 'error.title')}</Typography>
      <Typography variant="body2">{message}</Typography>
    </Alert>
  );
});

ErrorCard.displayName = 'ErrorCard';

const EmptyText = memo(({ text }: { text: string }) => (
  <TableRow>
    <TableCell colSpan={4}>
      <Stack alignItems="center" sx={{ py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          {text}
        </Typography>
      </Stack>
    </TableCell>
  </TableRow>
));

EmptyText.displayName = 'EmptyText';

const CenterBox = ({ children }: { children: ReactNode }) => (
  <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 360 }}>
    {children}
  </Stack>
);
